/** Admin Routes
 * Roles: ai_lab_admin
 *
 * Top-level meta (title/description/breadcrumb) is auto-resolved by the
 * i18n router guard from each route's `name` — see
 * `src/plugins/i18n/routeI18n.ts`. Child routes still use hard-coded
 * strings; migrate them into the map when they get i18n keys.
 */

import TenantsOverview from '../pages/settings/admin/Tenants/TenantsOverview.vue'
import UserOverview from '../pages/settings/admin/User/UserOverview.vue'
import AdminSettingsOverview from '../pages/settings/admin/AdminSettingsOverview.vue'
import DataExports from '../pages/settings/admin/Exports/DataExports.vue'
import TenantDetailView from '../pages/settings/admin/Tenants/TenantDetailView.vue'
import MigrationAdmin from '../pages/settings/admin/Migrations/MigrationAdmin.vue'
import GlobalMessageOverview from '../pages/settings/admin/GlobalMessage/GlobalMessageOverview.vue'
import GlobalMessageDetailView from '../pages/settings/admin/GlobalMessage/GlobalMessageDetailView.vue'
import GlobalMessageView from '../pages/settings/admin/GlobalMessage/GlobalMessageView.vue'
import TranslationOverview from '../pages/settings/admin/Translations/TranslationOverview.vue'
import CustomizationOverview from '../pages/settings/admin/Customization/CustomizationOverview.vue'
import CustomizationDetailView from '../pages/settings/admin/Customization/CustomizationDetailView.vue'
import CustomizationView from '../pages/settings/admin/Customization/CustomizationView.vue'
import ThemesView from '../pages/settings/admin/Themes/ThemesView.vue'
import ThemesOverview from '../pages/settings/admin/Themes/ThemesOverview.vue'
import ThemesDetailView from '../pages/settings/admin/Themes/ThemesDetailView.vue'
import TestingOverview from '../pages/settings/admin/Testing/TestingOverview.vue'
import TestingView from '../pages/settings/admin/Testing/TestingView.vue'
import TranslationDetailView from '@/pages/settings/admin/Translations/TranslationDetailView.vue'
import TranslationView from '@/pages/settings/admin/Translations/TranslationView.vue'
import MailSettings from '../pages/settings/admin/Mail/MailSettings.vue'
import ModulesAdmin from '../pages/settings/admin/Modules/ModulesAdmin.vue'
import { Role } from '@/types'

export default [
  {
    path: '/admin/settings',
    name: 'admin.settings',
    meta: {
      requiresAuth: true,
      roles: ['ai_lab_admin'] as Role[]
    },
    children: [
      {
        path: '',
        component: AdminSettingsOverview,
        name: 'Admin Settings',
        meta: {
          title: 'Overview',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        }
      },
      {
        path: 'mail',
        component: MailSettings,
        name: 'Mail Settings',
        meta: {
          title: 'Mail Settings',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        }
      },
      {
        path: 'modules',
        component: ModulesAdmin,
        name: 'Modules Admin',
        meta: {
          title: 'Module verwalten',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        }
      },
      {
        path: 'users',
        component: UserOverview,
        name: 'Users',
        meta: {
          title: 'Users',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        }
      },
      {
        path: 'tenants',
        meta: {
          title: 'Tenants',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        },
        children: [
          {
            path: '',
            name: 'Tenants',
            component: TenantsOverview,
            meta: {
              title: 'Tenants',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          },
          {
            path: ':id',
            component: TenantDetailView,
            name: 'Tenant Detail',
            meta: {
              title: 'Tenant Detail',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          }
        ]
      },

      {
        path: 'exports',
        component: DataExports,
        meta: {
          title: 'Exports',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        }
      },
      {
        path: 'migrations',
        component: MigrationAdmin,
        meta: {
          title: 'Migrationen',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        }
      },
      {
        path: 'globalMessage',
        component: GlobalMessageView,
        meta: {
          title: 'Global Messages',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        },
        children: [
          {
            path: '',
            component: GlobalMessageOverview,
            name: 'AllGlobalMessages',
            meta: {
              title: 'Global Messages',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          },
          {
            path: 'edit/:id',
            component: GlobalMessageDetailView,
            name: 'EditGlobalMessage',
            meta: {
              title: 'Edit Global Message',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          }
        ]
      },
      {
        path: 'customization',
        component: CustomizationView,
        meta: {
          title: 'Customization',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        },
        children: [
          {
            path: '',
            component: CustomizationOverview,
            name: 'CustomizationOverview',
            meta: {
              title: 'Overview',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          },
          {
            path: 'edit/:id',
            component: CustomizationDetailView,
            name: 'CustomizationDetailView',
            meta: {
              title: 'Edit Customization',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          }
        ]
      },
      {
        path: 'testing',
        component: TestingView,
        meta: {
          title: 'Testing',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        },
        children: [
          {
            path: '',
            component: TestingOverview,
            name: 'TestingOverview',
            meta: {
              title: 'Testing Overview',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          }
        ]
      },
      {
        path: 'themes',
        component: ThemesView,
        meta: {
          title: 'Themes',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        },
        children: [
          {
            path: '',
            component: ThemesOverview,
            name: 'ThemesOverview',
            meta: {
              title: 'Themes',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          },
          {
            path: 'edit/:id',
            component: ThemesDetailView,
            name: 'ThemesDetailView',
            meta: {
              title: 'Edit Theme',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          }
        ]
      },
      {
        path: 'translations',
        component: TranslationView,
        meta: {
          title: 'Translations',
          requiresAuth: true,
          roles: ['ai_lab_admin']
        },
        children: [
          {
            path: '',
            name: 'Translations',
            component: TranslationOverview,
            meta: {
              title: 'Translations',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          },
          {
            path: 'edit/:id',
            name: 'TranslationDetailView',
            component: TranslationDetailView,
            meta: {
              title: 'Edit Translation',
              requiresAuth: true,
              roles: ['ai_lab_admin']
            }
          }
        ]
      }
    ]
  }
]
